import type { ColumnDef } from "@tanstack/react-table"

import {
  ProductInventoryTransactionDateCell,
  ProductInventoryTransactionStatusCell,
  ProductInventoryTransactionTypeCell,
} from "../components"

import type { InventoryByTransactionItem } from "@/graphql/codegen/graphql"
import { COST_BREAKDOWN_DECIMAL_PRECISION } from "@/screens/Procurement/CostBreakdown/constants"
import { formatNumber } from "@/utils/number"
import { formatWarehouseLabel } from "@/utils/warehouse"

export const useProductInventoryTransactionColumns = (): ColumnDef<InventoryByTransactionItem>[] => {
  return [
    {
      accessorKey: "createdOn",
      header: "Date Created",
      cell: ({ row }) => <ProductInventoryTransactionDateCell date={row.original.createdOn} />,
      enableSorting: true,
    },
    {
      accessorKey: "warehouse",
      header: "Warehouse",
      cell: ({ row }) => (row.original.warehouseArea ? formatWarehouseLabel(row.original.warehouseArea) : ""),
      enableSorting: true,
    },
    {
      accessorKey: "supplier",
      header: "Customer / Supplier",
      cell: ({ row }) => row.original.supplier ?? "",
      enableSorting: true,
    },
    {
      accessorKey: "companyId",
      header: "Company ID",
      cell: ({ row }) => row.original.companyId ?? "",
      enableSorting: false,
    },
    {
      accessorKey: "orderNumber",
      header: "Order #",
      cell: ({ row }) => row.original.orderNumber ?? "",
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <ProductInventoryTransactionStatusCell status={row.original.status} />,
      enableSorting: true,
    },
    {
      accessorKey: "inOutDate",
      header: "In / Out",
      cell: ({ row }) => <ProductInventoryTransactionDateCell date={row.original.inOutDate} />,
      enableSorting: true,
    },
    {
      accessorKey: "inboundWeight",
      header: "Inbound Weight",
      cell: ({ row }) =>
        row.original.inboundWeight ? (
          <span className='text-tertiary-green-900'>{formatNumber(row.original.inboundWeight)}</span>
        ) : (
          ""
        ),
      enableSorting: false,
    },
    {
      accessorKey: "outboundWeight",
      header: "Outbound Weight",
      cell: ({ row }) =>
        row.original.outboundWeight ? (
          <span className='text-secondary-600'>{formatNumber(row.original.outboundWeight)}</span>
        ) : (
          ""
        ),
      enableSorting: false,
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => <ProductInventoryTransactionTypeCell type={row.original.type} />,
      enableSorting: true,
    },
    {
      accessorKey: "price",
      header: "Price",
      cell: ({ row }) =>
        row.original.price
          ? `$${formatNumber(row.original.price, COST_BREAKDOWN_DECIMAL_PRECISION, COST_BREAKDOWN_DECIMAL_PRECISION)}`
          : "",
      enableSorting: false,
    },
    {
      accessorKey: "margin",
      header: "Margin %",
      cell: ({ row }) => (row.original.margin ? `${formatNumber(row.original.margin, 2, 2)} %` : ""),
      enableSorting: false,
    },
  ]
}
